import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { stripCheckinTokenFromUrl } from './utils/checkinToken';
import PersonalInformation from './pages/PersonalInformation';
import ContactAndCommunication from './pages/ContactAndCommunication';
import AddressAndIdentity from './pages/AddressAndIdentity';
import EmergencyAndBilling from './pages/EmergencyAndBilling';
import CareAndInsurance from './pages/CareAndInsurance';
import MedicalHistory from './pages/MedicalHistory';
import WellnessCheck from './pages/WellnessCheck';
import ReviewAndSign from './pages/ReviewAndSign';
import Payments from './pages/Payments';
import Completion from './pages/Completion';
import { MasterDataProvider } from './contexts/MasterDataContext';
import { DemographicsProvider, useDemographics } from './contexts/DemographicsContext';
import { WorkflowProvider } from './contexts/WorkflowContext';
import { LoaderProvider } from './contexts/LoaderContext';
import DemographicsAlreadySubmitted from './components/DemographicsAlreadySubmitted';
import { useParams } from 'react-router-dom';

// Only demographics sub-pages reach QuestionnaireWrapper (independent pages bypass it via
// ConditionalWrapper), so once demographics is submitted/synced we replace whichever sub-page
// is being rendered with a read-only "already submitted" view. This is what makes back-nav into
// demographics (e.g. the back button on Medical History) land calmly instead of erroring.
const DemographicsGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { alreadySubmitted } = useDemographics();
  if (alreadySubmitted) {
    return <DemographicsAlreadySubmitted />;
  }
  return <>{children}</>;
};

const QuestionnaireWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { appointmentId } = useParams<{ appointmentId: string }>();

  return (
    <MasterDataProvider>
      <DemographicsProvider appointmentId={appointmentId}>
        <DemographicsGate>{children}</DemographicsGate>
      </DemographicsProvider>
    </MasterDataProvider>
  );
};

const ConditionalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isIndependentPage = location.pathname.includes('/payments') ||
                            location.pathname.includes('/medical-history') ||
                            location.pathname.includes('/wellness-check') ||
                            location.pathname.includes('/review-and-sign') ||
                            location.pathname.includes('/complete');
  
  if (isIndependentPage) {
    return <>{children}</>;
  }
  
  return <QuestionnaireWrapper>{children}</QuestionnaireWrapper>;
};

const QuestionnaireRoutes = () => {
  // STI-3024: the backend lands the patient here with `?t=<token>`. Keep it for the tab, then take it
  // out of the address bar. The axios interceptor also captures it lazily, so calls made by child
  // effects (which run before this one) already carry it.
  useEffect(() => {
    stripCheckinTokenFromUrl();
  }, []);

  return (
    <LoaderProvider>
      <WorkflowProvider>
        <ConditionalWrapper>
          <Routes>
            <Route path="personal-information" element={<PersonalInformation />} />
            <Route path="contact-and-communication" element={<ContactAndCommunication />} />
            <Route path="address-and-identity" element={<AddressAndIdentity />} />
            <Route path="emergency-and-billing" element={<EmergencyAndBilling />} />
            <Route path="care-and-insurance" element={<CareAndInsurance />} />
            <Route path="medical-history" element={<MedicalHistory />} />
            <Route path="wellness-check" element={<WellnessCheck />} />
            <Route path="review-and-sign" element={<ReviewAndSign />} />
            <Route path="payments" element={<Payments />} />
            <Route path="complete" element={<Completion />} />
          </Routes>
        </ConditionalWrapper>
      </WorkflowProvider>
    </LoaderProvider>
  );
};

export default QuestionnaireRoutes;